import React, { useEffect, useState } from "react";
import { parseISO, subYears, isAfter } from "date-fns";
import { getName } from "country-list";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@picklebet/ui";
import { Input } from "@picklebet/ui";
import { DateInput } from "@picklebet/ui";
import { MONTH_FIRST_COUNTRIES } from "@/utilities/locale";
import { Typography } from "@picklebet/ui";
import { Button } from "@picklebet/ui";
import { useShellStore } from "@/features/shell/stores/shellStore";
import { getGeoInfo, validateProfile } from "../../services/session";
import { toFormErrors, setFormErrors } from "../../utils/errors";
import config from "appConfig";
import { VALID_EMAIL } from "utilities/regex";
import { FooterContent } from "./FooterContent";
import { NativeSelect } from "@picklebet/ui";
import { getAllCountries } from "@/utilities/address";
import { telemetry } from "@/utilities/telemetry";
import {
  type CustomerLicence,
  CustomerLicenceOptions,
} from "@/features/auth/stores/authStore";
import { useMinimumDelay } from "@/hooks/ui/useMinimumDelay";
import { ExistingAccountPrompt } from "./ExistingAccountPrompt";

import * as styles from "./SignUpForm.module.scss";

type DetailsProps = {
  initialValues: FormValues;
  onComplete: (values: FormValues, licence: CustomerLicence | null) => void;
};

const nationalityOptions = getAllCountries();

const createSchema = (isIsleOfMan: boolean) =>
  z
    .object({
      countryOfResidence: z.string(),
      nationality: z.string().optional(),
      email: z
        .string()
        .min(1, "Email is required")
        .regex(VALID_EMAIL, "Please enter a valid email")
        .refine(
          (value) =>
            !value?.toLowerCase().endsWith(".con") &&
            !value?.toLowerCase().endsWith(".con.au"),
          {
            message:
              ".con is not a valid email domain, please check your email is correct",
          },
        ),
      firstName: z.string().min(1, "First name is required"),
      middleName: z.string().optional(),
      lastName: z.string().min(1, "Last name is required"),
      dateOfBirth: z
        .string()
        .min(1, "A valid date of birth is required")
        .refine(
          (val) => !val || isAfter(parseISO(val), subYears(new Date(), 150)),
          "Invalid DOB",
        ),
    })
    .superRefine((data, ctx) => {
      if (isIsleOfMan && !data.nationality) {
        ctx.addIssue({
          code: "custom",
          message: "Please choose a nationality",
          path: ["nationality"],
        });
      }
    });

export type FormValues = z.infer<ReturnType<typeof createSchema>>;

const Details = ({ initialValues, onComplete }: DetailsProps) => {
  const [isIsleOfMan, setIsIsleOfMan] = useState(!!initialValues.nationality);
  const [isCountryLoading, setIsCountryLoading] = useState(true);
  const [isLicenceLoading, setIsLicenceLoading] = useState(true);
  const [isEmailTaken, setIsEmailTaken] = useState(false);

  const isDeeplinkOverlayActive = useShellStore(
    (state) => state.isDeeplinkOverlayActive,
  );
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    watch,
    getValues,
    setError,
    setValue,
  } = useForm<FormValues>({
    resolver: zodResolver(createSchema(isIsleOfMan)),
    defaultValues: {
      ...initialValues,
      nationality: isIsleOfMan ? initialValues.nationality : undefined,
      dateOfBirth: initialValues.dateOfBirth ?? "",
    },
  });

  const showMiddleName = config.ENV !== "production";
  const countryOfResidence = watch("countryOfResidence");
  const showNationalityLoading = useMinimumDelay(isLicenceLoading);

  useEffect(() => {
    const fetchGeoInfo = async () => {
      // no need to fetch new data if navigating back
      if (initialValues?.countryOfResidence) {
        setIsCountryLoading(false);
        return;
      }

      const result = await getGeoInfo();

      if (result.ok && result.data?.twoLetterCountryCode) {
        const code = result.data.twoLetterCountryCode;
        setValue("countryOfResidence", code && getName(code) ? code : "AU");
      }
      setIsCountryLoading(false);
    };

    fetchGeoInfo();
  }, [initialValues]);

  useEffect(() => {
    if (isCountryLoading || !countryOfResidence) return;

    const handleCountryChange = async () => {
      // skip if the user navigates back and has not changed countryOfResidence
      if (
        countryOfResidence === initialValues?.countryOfResidence &&
        initialValues?.nationality
      ) {
        setIsIsleOfMan(true);
        setIsLicenceLoading(false);
        return;
      }

      setIsLicenceLoading(true);

      const [_, licence] = await validateProfile("", "", {
        country: countryOfResidence,
      });
      const isIOM = licence === CustomerLicenceOptions.ISLE_OF_MAN;
      setIsIsleOfMan(isIOM);
      setValue("nationality", isIOM ? countryOfResidence : undefined);
      setIsLicenceLoading(false);
    };

    handleCountryChange();
  }, [isCountryLoading, countryOfResidence, initialValues]);

  const handleEmailBlur = async () => {
    const email = getValues("email");
    if (!email) return;

    const [validation] = await validateProfile(email, "", {});
    const emailTakenError = validation.find(
      (error) => error.messageTemplate === "{email.already.exists}",
    );

    // prevent duplicate segment events for the same email validation error
    if (emailTakenError && !isEmailTaken) {
      setIsEmailTaken(true);
      telemetry.emailAlreadyExistsInSignUp({ email });
    }
  };

  const handleEmailChange = () => {
    if (!isEmailTaken) return;
    setIsEmailTaken(false);
  };

  return (
    <>
      <Form
        disabled={isSubmitting}
        onSubmit={handleSubmit(async (values: FormValues) => {
          const { email, ...profile } = values;
          const [validation, licence] = await validateProfile(email, "", {
            country: countryOfResidence,
            ...profile,
          });
          if (validation.length > 0) {
            setFormErrors(
              toFormErrors<FormValues>(validation, "profile"),
              setError,
            );
            return;
          }

          onComplete(values, licence);
        })}
      >
        <NativeSelect
          options={nationalityOptions}
          label={"Country of Residence"}
          errorMessage={errors?.countryOfResidence?.message}
          placeholder={"Select your country of residence"}
          {...register("countryOfResidence", {
            onChange: (e) => {
              telemetry.countryOfResidenceChange({
                fromCountry: countryOfResidence,
                toCountry: e.target.value,
              });
            },
          })}
        />
        {isIsleOfMan && (
          <>
            {/* keep label regardless of the loading state */}
            <Typography
              component="label"
              className={styles.nationality}
              noSpacing
            >
              Nationality
            </Typography>
            <NativeSelect
              options={nationalityOptions}
              errorMessage={errors?.nationality?.message}
              placeholder={"Select your nationality"}
              loading={showNationalityLoading}
              {...register("nationality")}
            />
          </>
        )}

        <Input
          autoFocus={!isDeeplinkOverlayActive}
          id={"email"}
          label="Email"
          type="email"
          placeholder="Email"
          {...register("email", {
            onChange: handleEmailChange,
            onBlur: handleEmailBlur,
          })}
          // this prevents zod shcema validation on other fields from overwriting the `Invalid Email` error
          errorMessage={
            isEmailTaken ? (
              <>
                Invalid Email.{" "}
                <ExistingAccountPrompt
                  className={styles.inlineExistingAccount}
                  isEmailTaken={isEmailTaken}
                />
              </>
            ) : (
              errors.email?.message
            )
          }
        />
        <Input
          id={"firstName"}
          label="First Name"
          type="text"
          placeholder="Must match your ID"
          {...register("firstName")}
          errorMessage={errors.firstName?.message}
        />
        {showMiddleName && (
          <Input
            id={"middleName"}
            label="Middle Name"
            type="text"
            placeholder="Middle Name"
            {...register("middleName")}
            errorMessage={errors.middleName?.message}
          />
        )}
        <Input
          label="Last Name"
          type="text"
          placeholder="Must match your ID"
          {...register("lastName")}
          errorMessage={errors.lastName?.message}
        />
        <Controller
          control={control}
          name={"dateOfBirth"}
          render={({ field: { ref: _, ...field } }) => (
            <DateInput
              id={"dateOfBirth"}
              label="When were you born?"
              {...field}
              monthFirst={MONTH_FIRST_COUNTRIES.has(countryOfResidence)}
              errorMessage={errors.dateOfBirth?.message}
            />
          )}
        />
        <Button loading={isSubmitting} type={`submit`}>
          Continue
        </Button>
      </Form>
      <div className={styles.footer}>
        <ExistingAccountPrompt isEmailTaken={isEmailTaken} includeBrand />
        <FooterContent />
      </div>
    </>
  );
};

export { Details };
